/**
 * OrderDesk API functions
 *
 * sendOrderToOrderDesk:
 *   - Submits a new order via POST.
 *
 * updateOrderInOrderDesk:
 *   - Updates an existing order via POST.
 *   - Removes DOCMatch_id from the payload and adds an isUpdate flag.
 *
 * Detailed logging is provided for debugging.
 */

import { calculateWholesaleShippingFromCart } from '../utils/wholesaleShippingUtils';
import { coerceCustomerPricingKey } from './pricingTierService';
import { resolveUserPhone } from '../utils/userPhoneFields';

const ORDERDESK_URL = 'https://submittoorderdesk-ar74xt4l6a-uc.a.run.app';

const sendOrderToOrderDesk = async (orderData) => {
    console.log("[sendOrderToOrderDesk] Starting new order submission...");
    try {
        console.log("[sendOrderToOrderDesk] Order data to be submitted:\n", JSON.stringify(orderData, null, 2));
        const response = await fetch(ORDERDESK_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(orderData),
        });
        console.log("[sendOrderToOrderDesk] HTTP response status:", response.status);
        if (!response.ok) {
            let errorData = {};
            try {
                errorData = await response.json();
            } catch (parseError) {
                console.error("[sendOrderToOrderDesk] Error parsing error response:", parseError);
            }
            console.error("[sendOrderToOrderDesk] Error response data:\n", errorData);
            throw new Error(errorData.message || 'Failed to submit order to OrderDesk.');
        }
        const data = await response.json();
        console.log("[sendOrderToOrderDesk] Order submitted successfully. Response data:\n", data);
        return data;
    } catch (error) {
        console.error("[sendOrderToOrderDesk] Error during new order submission:", error);
        alert('There was a problem submitting your order. Please try again later.');
        return null;
    }
};

export async function updateOrderInOrderDesk(updatedOrderData) {
    console.log("[updateOrderInOrderDesk] Starting order update submission...");
    try {
        // Remove DOCMatch_id if present so it isn�t sent during an update.
        if (updatedOrderData.hasOwnProperty("DOCMatch_id")) {
            console.log("[updateOrderInOrderDesk] Removing DOCMatch_id from payload.");
            delete updatedOrderData.DOCMatch_id;
        }
        // Mark this payload as an update.
        updatedOrderData.isUpdate = true;
        console.log("[updateOrderInOrderDesk] Final update payload:\n", JSON.stringify(updatedOrderData, null, 2));

        // Use POST for updates (since PUT is blocked by CORS).
        const response = await fetch(ORDERDESK_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updatedOrderData),
        });
        console.log("[updateOrderInOrderDesk] HTTP response status:", response.status);
        if (!response.ok) {
            let errorData = {};
            try {
                errorData = await response.json();
            } catch (parseError) {
                console.error("[updateOrderInOrderDesk] Error parsing error response:", parseError);
            }
            console.error("[updateOrderInOrderDesk] Error response data:\n", errorData);
            throw new Error(errorData.message || 'Failed to update order in OrderDesk.');
        }
        const data = await response.json();
        console.log("[updateOrderInOrderDesk] Order update successful. Response data:\n", data);
        return data;
    } catch (error) {
        console.error("[updateOrderInOrderDesk] Error during update submission:", error);
        alert('There was a problem updating your order in OrderDesk. Please try again.');
        return null;
    }
}

/**
 * Helper: Resend Entire Order to OrderDesk for updates.
 * This function rebuilds the final update payload using Firestore order data.
 * It uses the customer data stored in the order (i.e. the user who originally placed the order)
 * to fill every field except the order_items (which can be updated) and then submits the update.
 */
const resendEntireOrderToOrderDesk = async (orderId) => {
    console.log(`[resendEntireOrderToOrderDesk] Preparing to update order ${orderId} in OrderDesk...`);
    try {
        // Dynamically import Firestore functions
        const { doc, getDoc } = require("firebase/firestore");
        const orderRef = doc(require('../services/firebase').db, 'orders', orderId);
        const snap = await getDoc(orderRef);
        if (!snap.exists()) {
            console.error(`[resendEntireOrderToOrderDesk] No order found with ID ${orderId}`);
            return;
        }
        const docData = snap.data();
        const customerData = docData.customer;
        // Assume customerData.fullName is available; otherwise use first_name/last_name.
        const splitNames = customerData.fullName ?
            (function () {
                const parts = customerData.fullName.split(' ');
                return { first: parts[0] || 'Unknown', last: parts.slice(1).join(' ') || 'Unknown' };
            })() : { first: customerData.first_name || 'Unknown', last: customerData.last_name || 'Unknown' };

        const shippingInfo = docData.shipping || {};
        const isCollection =
            docData.collection === true ||
            /collection/i.test(String(docData.shipping_method || ''));
        const pricingKey = coerceCustomerPricingKey(
            docData.pricingKey || docData.Pricing || customerData.Pricing
        );
        const { shippingCharge: shippingTotal, orderTotalGbp } =
            calculateWholesaleShippingFromCart({
                lineItems: docData.order_items || [],
                isCollection,
                pricingKey,
            });

        const finalOrderData = {
            id: orderId,
            source_id: orderId,
            customer_id: customerData.companyName,
            email: customerData.email,
            delivery_date: docData.delivery_date || '',
            shipping_method: docData.shipping_method || '',
            shipping_total: shippingTotal,
            handling_total: 0,
            tax_total: 0,
            order_total: orderTotalGbp.toFixed(2),
            folder_id: docData.folder_id || '',
            date_added: docData.date_added || '',
            date_updated: new Date().toISOString(),
            status: docData.status || '',
            customer: {
                first_name: splitNames.first,
                last_name: splitNames.last,
                email: customerData.email,
                company: shippingInfo.companyName || customerData.companyName || '',
            },
            billing: docData.billing ? {
                first_name: splitNames.first,
                last_name: splitNames.last,
                company: docData.billing.company || customerData.companyName || '',
                address1: docData.billing.address1 || '',
                address2: docData.billing.address2 || '',
                address3: docData.billing.address3 || '',
                city: docData.billing.city || '',
                state: docData.billing.state || '',
                postal_code: docData.billing.postal_code || '',
                phone: resolveUserPhone(customerData),
            } : null,
            shipping: {
                first_name: splitNames.first,
                last_name: splitNames.last,
                company: shippingInfo.companyName || customerData.companyName || '',
                address1: shippingInfo.line1 || '',
                address2: shippingInfo.line2 || '',
                address3: shippingInfo.line3 || shippingInfo.addressLine3 || '',
                city: shippingInfo.city || '',
                state: shippingInfo.state || '',
                postal_code: shippingInfo.postCode || shippingInfo.postalCode || '',
                phone: shippingInfo.phone || '',
            },
            order_items: docData.order_items || [],
            Notes: docData.Notes || [
                {
                    date_added: new Date().toISOString(),
                    username: customerData.email,
                    content: docData.orderNote || 'No additional notes.',
                },
            ],
            checkout_data: docData.checkout_data || {},
            isUpdate: true,
        };

        console.log("[resendEntireOrderToOrderDesk] Final update payload for OrderDesk:\n", JSON.stringify(finalOrderData, null, 2));
        const updateResult = await updateOrderInOrderDesk(finalOrderData);
        console.log(`[resendEntireOrderToOrderDesk] Order ${orderId} updated successfully in OrderDesk! Update result:\n`, updateResult);
    } catch (error) {
        console.error("[resendEntireOrderToOrderDesk] Error updating order:", error);
        if (error.response) {
            console.error("[resendEntireOrderToOrderDesk] Error response data:\n", error.response.data);
        }
    }
};

export { resendEntireOrderToOrderDesk };
export default sendOrderToOrderDesk;
